import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { type Product } from '@/constants/products'
import { cn } from '@/lib/cn'
import {
  Barcode,
  CheckCircle2,
  Navigation,
  FileSpreadsheet,
  Printer,
  ShoppingBag,
  RotateCcw,
  Zap,
} from 'lucide-react'

interface ProductMediaViewportProps {
  product: Product
}

export function ProductMediaViewport({ product }: ProductMediaViewportProps) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [actionTriggered, setActionTriggered] = useState(false)

  const handleTriggerAction = () => {
    if (isProcessing) return
    setIsProcessing(true)
    setTimeout(() => {
      setIsProcessing(false)
      setActionTriggered((prev) => !prev)
    }, 600)
  }

  return (
    <Card
      surface="2"
      className="relative overflow-hidden p-0 border-border shadow-xl flex flex-col justify-between min-h-[380px] sm:min-h-[420px]"
    >
      {/* Top Window Bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-3/50 select-none">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            <span className="w-2.5 h-2.5 rounded-full bg-border-strong/70 inline-block" />
            <span className="w-2.5 h-2.5 rounded-full bg-border-strong/70 inline-block" />
            <span className="w-2.5 h-2.5 rounded-full bg-border-strong/70 inline-block" />
          </div>
          <span className="font-mono text-[11px] text-fg-muted uppercase tracking-wider ml-1">
            {product.id} // live-runtime
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase px-2 py-0.5 rounded-xs bg-surface-1 text-fg border border-border-subtle flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            Online Node
          </span>
        </div>
      </div>

      {/* Main Viewport Content depending on product */}
      <div className="p-4 sm:p-6 flex-1 flex flex-col justify-between">
        {product.id === 'suke' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
              <div className="flex items-center gap-2">
                <Barcode className="w-4 h-4 text-fg" />
                <span className="font-mono text-xs font-semibold text-fg">POS Till 01</span>
              </div>
              <span className="font-mono text-[11px] text-fg-muted">
                Offline Buffer: {actionTriggered ? '3 items queued' : '2 items queued'}
              </span>
            </div>

            <div className="space-y-2 font-mono text-xs">
              <div className="flex justify-between p-2.5 rounded-xs bg-surface-1 border border-border-subtle">
                <span className="text-fg">1x Ethiopian Linen Shirt</span>
                <span className="font-semibold text-fg">ETB 1,200.00</span>
              </div>
              <div className="flex justify-between p-2.5 rounded-xs bg-surface-1 border border-border-subtle">
                <span className="text-fg">1x Artisan Leather Loafers</span>
                <span className="font-semibold text-fg">ETB 3,450.00</span>
              </div>
              {actionTriggered && (
                <div className="flex justify-between p-2.5 rounded-xs bg-surface-1 border border-success/40 transition-all duration-300">
                  <span className="text-fg flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                    1x Cotton Chino Trousers (Scanned)
                  </span>
                  <span className="font-semibold text-fg">ETB 1,850.00</span>
                </div>
              )}
            </div>

            <div className="p-3 rounded-xs bg-surface-1 border border-border-subtle flex items-center justify-between font-mono">
              <span className="text-xs text-fg-subtle">TOTAL PAYABLE:</span>
              <span className="text-base font-bold text-fg">
                {actionTriggered ? 'ETB 6,500.00' : 'ETB 4,650.00'}
              </span>
            </div>
          </div>
        )}

        {product.id === 'derash-ride' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
              <div className="flex items-center gap-2">
                <Navigation className="w-4 h-4 text-fg" />
                <span className="font-mono text-xs font-semibold text-fg">Dispatch Console</span>
              </div>
              <span className="font-mono text-[11px] text-fg-muted">Zone: Bole Medhanialem</span>
            </div>

            <div className="space-y-2 font-mono text-xs">
              <div className="p-3 rounded-xs bg-surface-1 border border-border-subtle space-y-1.5">
                <div className="flex justify-between text-fg-muted text-[11px]">
                  <span>REQUEST #TRIP-9041</span>
                  <span className="text-fg font-medium">Bole Road → Kazanchis</span>
                </div>
                <div className="flex justify-between items-center pt-1 border-t border-border-subtle">
                  <span className="text-fg-subtle">Estimated Tariff:</span>
                  <span className="font-bold text-fg">ETB 320.00</span>
                </div>
              </div>

              <div
                className={cn(
                  'p-3 rounded-xs border transition-all duration-300 font-mono text-xs',
                  actionTriggered
                    ? 'bg-surface-1 border-success/40'
                    : 'bg-surface-1 border-border-subtle'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-fg-subtle">Dispatcher State:</span>
                  <span
                    className={cn(
                      'font-semibold flex items-center gap-1.5',
                      actionTriggered ? 'text-success' : 'text-fg-muted'
                    )}
                  >
                    {actionTriggered ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                        Driver Assigned: DRV-109 (Suzuki Dzire)
                      </>
                    ) : (
                      'Idle // Telemetry Standby'
                    )}
                  </span>
                </div>
                {actionTriggered && (
                  <p className="text-[11px] text-fg-subtle mt-1.5 pt-1.5 border-t border-border-subtle">
                    ETA: 3.2 minutes • Telebirr fare lock authorized • GPS socket active
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {product.id === 'hun-core-erp' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-fg" />
                <span className="font-mono text-xs font-semibold text-fg">Ledger & Payroll</span>
              </div>
              <span className="font-mono text-[11px] text-fg-muted">Run: Meskerem 2017</span>
            </div>

            <div className="space-y-2 font-mono text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2.5 rounded-xs bg-surface-1 border border-border-subtle">
                  <span className="text-[10px] text-fg-muted block uppercase">Gross Payroll</span>
                  <span className="font-bold text-fg text-sm">ETB 1,480,000</span>
                </div>
                <div className="p-2.5 rounded-xs bg-surface-1 border border-border-subtle">
                  <span className="text-[10px] text-fg-muted block uppercase">ERCA Tax Hold</span>
                  <span className="font-bold text-fg text-sm">ETB 296,400</span>
                </div>
              </div>

              <div
                className={cn(
                  'p-3 rounded-xs border transition-all duration-300 font-mono text-xs',
                  actionTriggered
                    ? 'bg-surface-1 border-success/40'
                    : 'bg-surface-1 border-border-subtle'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-fg-subtle">Ledger Balancing:</span>
                  <span
                    className={cn(
                      'font-semibold flex items-center gap-1.5',
                      actionTriggered ? 'text-success' : 'text-fg-muted'
                    )}
                  >
                    {actionTriggered ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                        Double-Entry Verified (0.00 Variance)
                      </>
                    ) : (
                      'Pending Statutory Audit'
                    )}
                  </span>
                </div>
                {actionTriggered && (
                  <p className="text-[11px] text-fg-subtle mt-1.5 pt-1.5 border-t border-border-subtle">
                    Employee Pension (7%) & Employer (11%) posted to General Ledger
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {product.id === 'hun-speed-pos' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
              <div className="flex items-center gap-2">
                <Printer className="w-4 h-4 text-fg" />
                <span className="font-mono text-xs font-semibold text-fg">Counter Lane 04</span>
              </div>
              <span className="font-mono text-[11px] text-fg-muted">ESC/POS 9100 Ready</span>
            </div>

            <div className="space-y-2 font-mono text-xs">
              <div className="p-2.5 rounded-xs bg-surface-1 border border-border-subtle space-y-1">
                <div className="flex justify-between">
                  <span className="text-fg-muted">SCANNER: HID-OPTICAL</span>
                  <span className="text-success font-semibold">STANDBY</span>
                </div>
                <div className="flex justify-between text-fg">
                  <span>SCALE: RS-232</span>
                  <span>1.450 KG (ZERO BALANCED)</span>
                </div>
              </div>

              <div
                className={cn(
                  'p-3 rounded-xs border transition-all duration-300 font-mono text-xs',
                  actionTriggered
                    ? 'bg-surface-1 border-success/40'
                    : 'bg-surface-1 border-border-subtle'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-fg-subtle">Thermal Spooler:</span>
                  <span
                    className={cn(
                      'font-semibold flex items-center gap-1.5',
                      actionTriggered ? 'text-success' : 'text-fg-muted'
                    )}
                  >
                    {actionTriggered ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                        Slip Printed & Drawer Kicked
                      </>
                    ) : (
                      'Spool Idle // Awaiting Barcode'
                    )}
                  </span>
                </div>
                {actionTriggered && (
                  <p className="text-[11px] text-fg-subtle mt-1.5 pt-1.5 border-t border-border-subtle">
                    Payload sent in 18ms • Cash drawer pulse 24V dispatched
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {product.id === 'hun-commerce' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-border-subtle">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-fg" />
                <span className="font-mono text-xs font-semibold text-fg">Order Fulfillment</span>
              </div>
              <span className="font-mono text-[11px] text-fg-muted">D2C Mobile Storefront</span>
            </div>

            <div className="space-y-2 font-mono text-xs">
              <div className="p-2.5 rounded-xs bg-surface-1 border border-border-subtle flex justify-between items-center">
                <div>
                  <span className="text-fg font-semibold block">Order #ORD-7719</span>
                  <span className="text-[11px] text-fg-muted">Minimalist Canvas Backpack</span>
                </div>
                <span className="font-bold text-fg">ETB 2,400.00</span>
              </div>

              <div
                className={cn(
                  'p-3 rounded-xs border transition-all duration-300 font-mono text-xs',
                  actionTriggered
                    ? 'bg-surface-1 border-success/40'
                    : 'bg-surface-1 border-border-subtle'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-fg-subtle">Fulfillment Status:</span>
                  <span
                    className={cn(
                      'font-semibold flex items-center gap-1.5',
                      actionTriggered ? 'text-success' : 'text-fg-muted'
                    )}
                  >
                    {actionTriggered ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                        Dispatched & Customer SMS Sent
                      </>
                    ) : (
                      'Authorized via Telebirr'
                    )}
                  </span>
                </div>
                {actionTriggered && (
                  <p className="text-[11px] text-fg-subtle mt-1.5 pt-1.5 border-t border-border-subtle">
                    Courier pickup scheduled • Packing slip auto-generated
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Button Section */}
        <div className="pt-4 mt-4 border-t border-border-subtle flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleTriggerAction}
            disabled={isProcessing}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-sm text-xs font-mono font-medium tracking-tight transition-all duration-150 cursor-pointer select-none border',
              actionTriggered
                ? 'bg-surface-3 text-fg border-border hover:bg-surface-4'
                : 'bg-btn-primary text-btn-primary-fg border-transparent hover:bg-btn-primary-hover shadow-xs'
            )}
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                Executing Pipeline...
              </span>
            ) : actionTriggered ? (
              <>
                <RotateCcw className="w-3.5 h-3.5" />
                Reset Simulation State
              </>
            ) : (
              <>
                <Zap className="w-3.5 h-3.5" />
                {product.id === 'suke' && 'Trigger Barcode Scan'}
                {product.id === 'derash-ride' && 'Simulate Rider Dispatch'}
                {product.id === 'hun-core-erp' && 'Audit Statutory Payroll'}
                {product.id === 'hun-speed-pos' && 'Trigger Laser Scan & Print'}
                {product.id === 'hun-commerce' && 'Dispatch Order & Send SMS'}
              </>
            )}
          </button>
        </div>
      </div>
    </Card>
  )
}
